import React, { FC, ReactElement, useCallback, useMemo } from 'react'
import { DeviceModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { getDeviceList } from '../../Common/API/ItemsGetters'
import { DataTableColumn } from '../../../ReactRouting/Components/Molecules/DataTable/DataTable'
import { DEVICE_ADMIN_LIST_FIELDS } from '../../../NonReact/constants/ApiConstants'
import { DeviceListItem, DeviceListResponse } from '../../../NonReact/Types/DeviceListTypes'
import { DeviceMemberRow } from '../Types/Types'
import { MemberTable, MemberSearchOption } from './MemberTable'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import type { RangeEntryWrapper } from '../../Common/Components/Selects/RangeCombo/Types'
import { buildRangeFilterExpression } from '../Utils/buildRangeFilterExpression'

const INITIAL_MEMBER_OPTIONS_LIMIT: number = 50
const SEARCH_MEMBER_OPTIONS_LIMIT: number = 100
const RANGE_SEARCH_PATTERN: RegExp = /^\d*[xX]?\d*-\d*[xX]?\d*$/

type Props = {
    viewOnly?: boolean
    filterTelSysID: number
    filterPartitionID: number
    /**
     * Device number of the device open in the modal (change/view). When set, that device is omitted from async
     * member search so a hunt group cannot list itself as a member.
     */
    openModalDeviceNumber?: string
    selectedRows: DeviceMemberRow[]
    onRowsChange: (rows: DeviceMemberRow[]) => void
    isRouterMode?: boolean
}

/**
 * Formats a device list row for the async member search options.
 *
 * @param row - Admin device list item.
 * @returns Single-line label text.
 */
const formatDeviceOptionLabel = (row: DeviceListItem): string => {
    const deviceNumber: string = row.DeviceNumber ?? EMPTY_STRING
    const description: string = row.Description?.trim() ?? EMPTY_STRING

    return isStringNotEmpty(description) ? `${deviceNumber} - ${description}` : deviceNumber
}

/**
 * <p>Decides whether a device list row is eligible for hunt-group member search, excluding the device under edit.</p>
 *
 * @param deviceNumber - Candidate device number from the list API.
 * @param openModalDeviceNumber - Device currently being edited in the modal, if any.
 * @returns Whether {@code deviceNumber} should appear in member search results.
 */
const shouldIncludeDeviceInMemberSearch = (
    deviceNumber: Definable<string>,
    openModalDeviceNumber: Definable<string>
): boolean => {
    if (!isStringNotEmpty((deviceNumber ?? EMPTY_STRING).trim())) {
        return false
    }

    if (!isStringNotEmpty((openModalDeviceNumber ?? EMPTY_STRING).trim())) {
        return true
    }

    return (deviceNumber ?? EMPTY_STRING).trim() !== (openModalDeviceNumber ?? EMPTY_STRING).trim()
}

/**
 * Device Members tab for group devices using a paginated table plus async add-search control.
 *
 * @param props - Tab props including TelSys / partition scope and current member rows.
 * @returns Device-members management UI.
 */
export const DeviceMembersTab: FC<Props> = ({
    viewOnly,
    filterTelSysID,
    filterPartitionID,
    openModalDeviceNumber,
    selectedRows,
    onRowsChange,
    isRouterMode
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    const columns: Array<DataTableColumn<DeviceMemberRow, keyof DeviceMemberRow>> = useMemo(
        (): Array<DataTableColumn<DeviceMemberRow, keyof DeviceMemberRow>> => [
            {
                field: 'DeviceNumber',
                key: 'DeviceNumber',
                headerName: language.get(DeviceModal.DeviceModal_Members_DeviceNumber)
            },
            {
                field: 'Description',
                key: 'Description',
                headerName: language.get(DeviceModal.DeviceModal_Members_Description)
            }
        ],
        [language]
    )

    const loadOptions = useCallback(
        async (searchQuery: string): Promise<Array<MemberSearchOption<DeviceListItem>>> => {
            const isInitialOpenQuery: boolean = !isStringNotEmpty(searchQuery)

            if (!isInitialOpenQuery && searchQuery.length < 2) return []

            const isRangeQuery: boolean = !isInitialOpenQuery && RANGE_SEARCH_PATTERN.test(searchQuery)

            const response: Nullable<DeviceListResponse> = await getDeviceList(
                filterTelSysID,
                filterPartitionID,
                0,
                isInitialOpenQuery ? INITIAL_MEMBER_OPTIONS_LIMIT : SEARCH_MEMBER_OPTIONS_LIMIT,
                isRangeQuery ? undefined : 'DeviceNumber:ASC',
                isRangeQuery || isInitialOpenQuery ? undefined : searchQuery,
                undefined,
                isRangeQuery ? searchQuery : undefined,
                isRangeQuery ? undefined : DEVICE_ADMIN_LIST_FIELDS
            )

            return (response?.Data?.Items ?? [])
                .filter((row: DeviceListItem): boolean =>
                    shouldIncludeDeviceInMemberSearch(row.DeviceNumber, openModalDeviceNumber)
                )
                .map((row: DeviceListItem) => ({
                    label: formatDeviceOptionLabel(row),
                    value: row.DeviceNumber ?? EMPTY_STRING,
                    original: row
                }))
        },
        [filterPartitionID, filterTelSysID, openModalDeviceNumber]
    )

    const resolveValidIdentifiers = useCallback(
        async (
            pendingRanges: Nullable<RangeEntryWrapper[]>
        ): Promise<ReadonlyArray<MemberSearchOption<DeviceListItem>>> => {
            const filterExpression: string = buildRangeFilterExpression(pendingRanges)

            if (!filterExpression) return []

            const response: Nullable<DeviceListResponse> = await getDeviceList(
                filterTelSysID,
                filterPartitionID,
                0,
                1000,
                undefined,
                undefined,
                undefined,
                filterExpression
            )

            return (response?.Data?.Items ?? [])
                .filter((row: DeviceListItem): boolean =>
                    shouldIncludeDeviceInMemberSearch(row.DeviceNumber, openModalDeviceNumber)
                )
                .map(
                    (row: DeviceListItem): MemberSearchOption<DeviceListItem> => ({
                        value: row.DeviceNumber ?? EMPTY_STRING,
                        label: formatDeviceOptionLabel(row),
                        original: row
                    })
                )
        },
        [filterPartitionID, filterTelSysID, openModalDeviceNumber]
    )

    return (
        <div className='device-members-tab tab-area'>
            <MemberTable
                label={language.get(DeviceModal.DeviceModal_Tab_DeviceMembers)}
                noRowsLabel={language.get(DeviceModal.DeviceModal_Members_NoDeviceMembers)}
                searchAddButtonTitle={language.get(DeviceModal.DeviceModal_Members_AddHotkey)}
                removeButtonLabel={language.get(DeviceModal.DeviceModal_Members_Remove)}
                searchLabel={language.get(DeviceModal.DeviceModal_Members_Add)}
                rows={selectedRows}
                onRowsChange={onRowsChange}
                columns={columns}
                getIdentifier={(row: DeviceMemberRow): string => row.DeviceNumber}
                createRowFromOption={(option): DeviceMemberRow => ({
                    key: option.value,
                    DeviceNumber: option.value,
                    Description: option.original?.Description ?? EMPTY_STRING
                })}
                loadOptions={loadOptions}
                viewOnly={viewOnly}
                isRouterMode={isRouterMode}
                resolveValidIdentifiers={resolveValidIdentifiers}
            />
        </div>
    )
}
